import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Check, 
  ArrowRight, 
  Zap, 
  TrendingUp, 
  Crown, 
  Instagram, 
  Facebook, 
  Twitter,
  Linkedin,
  Youtube,
  Camera,
  BarChart3,
  MessageSquare,
  Users,
  Calendar,
  Target,
  Sparkles
} from 'lucide-react';

const SocialMedia = () => {
  const pulseFeatures = [
    "2 Posts per week",
    "2 Social platforms (Instagram + Facebook)",
    "Basic graphic design",
    "Professional caption writing",
    "Strategic hashtag research",
    "Content calendar planning",
    "Brand-consistent visuals",
    "Monthly performance summary"
  ];

  const amplifierFeatures = [
    "Everything in The Pulse",
    "4-5 Posts per week",
    "3 Social platforms coverage",
    "2 Reels/Short-form videos monthly",
    "Community management",
    "Comment & DM responses",
    "Detailed monthly reports",
    "Competitor analysis",
    "Trending hashtag optimization",
    "Story content creation",
    "Engagement strategy",
    "Growth optimization"
  ];

  const platforms = [
    { name: "Instagram", icon: <Instagram className="h-6 w-6" />, color: "text-pink-500" },
    { name: "Facebook", icon: <Facebook className="h-6 w-6" />, color: "text-blue-600" },
    { name: "Twitter/X", icon: <Twitter className="h-6 w-6" />, color: "text-blue-400" },
    { name: "LinkedIn", icon: <Linkedin className="h-6 w-6" />, color: "text-blue-700" },
    { name: "YouTube", icon: <Youtube className="h-6 w-6" />, color: "text-red-600" },
    { name: "TikTok", icon: <Camera className="h-6 w-6" />, color: "text-black" }
  ];

  const contentTypes = [
    {
      title: "Visual Content",
      description: "Eye-catching graphics and photos that stop the scroll",
      icon: <Camera className="h-6 w-6 text-accent" />,
      examples: ["Product photography", "Behind-the-scenes", "Infographics", "Quote cards"]
    },
    {
      title: "Video Content", 
      description: "Engaging Reels, Stories, and short-form videos",
      icon: <Youtube className="h-6 w-6 text-accent" />,
      examples: ["Product demos", "Team introductions", "Process videos", "Customer testimonials"]
    },
    {
      title: "Community Building",
      description: "Authentic engagement that builds lasting relationships",
      icon: <Users className="h-6 w-6 text-accent" />,
      examples: ["Comment responses", "DM management", "User-generated content", "Community polls"]
    },
    {
      title: "Strategic Planning",
      description: "Data-driven content strategy for maximum impact",
      icon: <Target className="h-6 w-6 text-accent" />,
      examples: ["Content calendars", "Hashtag research", "Competitor analysis", "Performance tracking"]
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-primary/10 text-primary border-primary/20 font-body mb-4">
            Social Media Marketing
          </Badge>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground mb-6">
            Done-For-You Social Media
            <span className="text-gradient"> That Actually Works</span>
          </h1>
          <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Stop struggling with social media. Our monthly retainer services handle everything 
            from content creation to community management, so you can focus on running your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold"
            >
              <Link to="/booking">
                Get Social Media Audit
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button 
              asChild 
              variant="outline" 
              size="lg" 
              className="font-heading font-semibold"
            >
              <Link to="#packages">View Packages</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Platform Coverage */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              We Cover All Major Platforms
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              From Instagram to LinkedIn, we know what works on each platform and 
              tailor your content accordingly.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {platforms.map((platform, index) => (
              <Card key={index} className="hover-lift shadow-card text-center">
                <CardContent className="p-6">
                  <div className={`${platform.color} mb-3 flex justify-center`}>
                    {platform.icon}
                  </div>
                  <h3 className="font-heading font-semibold text-foreground text-sm">
                    {platform.name}
                  </h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Packages */}
      <section id="packages" className="section-padding bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Choose Your Social Media Package
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              Monthly retainer services that save you time and deliver consistent results.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            
            {/* The Pulse Package */}
            <Card className="relative hover-lift shadow-card border-2 border-border">
              <CardHeader className="text-center pb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="font-heading text-2xl font-bold text-foreground">The Pulse</CardTitle>
                <p className="font-body text-muted-foreground">Perfect for consistent brand presence</p>
                <div className="mt-4">
                  <span className="font-heading text-4xl font-bold text-primary">$600</span>
                  <span className="font-body text-muted-foreground ml-2">- $1,200/mo</span>
                </div>
                <p className="font-body text-xs text-muted-foreground mt-2">
                  Steady content flow to keep your audience engaged
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {pulseFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <Check className="h-4 w-4 text-accent flex-shrink-0" />
                      <span className="font-body text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="pt-4">
                  <Button 
                    asChild 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold"
                  >
                    <Link to="/booking">
                      Start The Pulse
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>

                <div className="text-center pt-2">
                  <p className="font-body text-xs text-muted-foreground">
                    Best for: Small businesses, service providers, consultants
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* The Amplifier Package */}
            <Card className="relative hover-lift shadow-elegant border-2 border-accent bg-gradient-to-br from-white to-accent/5">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-accent text-accent-foreground font-body font-semibold px-4 py-1">
                  Most Popular
                </Badge>
              </div>
              <CardHeader className="text-center pb-8">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-accent" />
                </div>
                <CardTitle className="font-heading text-2xl font-bold text-foreground">The Amplifier</CardTitle>
                <p className="font-body text-muted-foreground">Complete social media management</p>
                <div className="mt-4">
                  <span className="font-heading text-4xl font-bold text-accent">$1,500</span>
                  <span className="font-body text-muted-foreground ml-2">- $3,500/mo</span>
                </div>
                <p className="font-body text-xs text-muted-foreground mt-2">
                  Full-service social media with video content
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  {amplifierFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <Check className="h-4 w-4 text-accent flex-shrink-0" />
                      <span className="font-body text-sm text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="pt-4">
                  <Button 
                    asChild 
                    className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-heading font-semibold"
                  >
                    <Link to="/booking">
                      Start The Amplifier
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>

                <div className="text-center pt-2">
                  <p className="font-body text-xs text-muted-foreground">
                    Best for: Growing businesses, e-commerce, professional services
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Custom Package Option */}
          <div className="mt-12 text-center">
            <Card className="max-w-2xl mx-auto shadow-elegant bg-gradient-to-r from-primary/10 to-accent/10">
              <CardContent className="p-8">
                <Crown className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold text-foreground mb-3">
                  Need Something Custom?
                </h3>
                <p className="font-body text-muted-foreground mb-6">
                  Large businesses, agencies, and enterprises often need tailored social media strategies. 
                  Let's discuss a custom package that fits your unique needs.
                </p>
                <Button 
                  asChild 
                  variant="outline" 
                  className="font-heading font-semibold"
                >
                  <Link to="/booking">Discuss Custom Package</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Content Types */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              What We Create For You
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              Every piece of content is strategically designed to engage your audience 
              and drive business results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {contentTypes.map((type, index) => (
              <Card key={index} className="hover-lift shadow-card">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                        {type.icon}
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-heading text-lg font-bold text-foreground mb-2">
                        {type.title}
                      </h3>
                      <p className="font-body text-muted-foreground mb-4">
                        {type.description}
                      </p>
                      <div className="space-y-1">
                        {type.examples.map((example, exampleIndex) => (
                          <div key={exampleIndex} className="flex items-center space-x-2">
                            <Sparkles className="h-3 w-3 text-accent" />
                            <span className="font-body text-sm text-foreground">{example}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Our Social Media Process</h2>
            <p className="font-body text-lg text-primary-foreground/80 max-w-2xl mx-auto">
              A proven system that delivers consistent results month after month.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">1. Strategy</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                We analyze your audience and create a custom content strategy.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">2. Planning</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                Monthly content calendars ensure consistent, strategic posting.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Camera className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">3. Creation</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                Professional content creation that aligns with your brand.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-8 w-8 text-accent-foreground" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">4. Optimize</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                Monthly reports and optimization based on performance data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Social Media Matters */}
      <section className="section-padding bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Why Social Media Marketing Works
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              Social media isn't just about likes and follows - it's about building relationships 
              that turn into customers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="hover-lift shadow-card text-center">
              <CardContent className="p-6">
                <Users className="h-8 w-8 text-accent mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-foreground mb-2">Build Trust</h3>
                <p className="font-body text-sm text-muted-foreground">
                  Regular, authentic content builds trust with your audience before they're ready to buy.
                </p>
              </CardContent>
            </Card>

            <Card className="hover-lift shadow-card text-center">
              <CardContent className="p-6">
                <Target className="h-8 w-8 text-accent mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-foreground mb-2">Stay Top-of-Mind</h3>
                <p className="font-body text-sm text-muted-foreground">
                  Consistent presence ensures your business is remembered when customers are ready to purchase.
                </p>
              </CardContent>
            </Card>

            <div className="text-center">
              <Card className="hover-lift shadow-card text-center">
                <CardContent className="p-6">
                  <TrendingUp className="h-8 w-8 text-accent mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-foreground mb-2">Drive Sales</h3>
                  <p className="font-body text-sm text-muted-foreground">
                    Strategic content and community engagement directly translate to increased revenue.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-accent/10 to-primary/10">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              Ready to Dominate Social Media?
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              Stop struggling with social media and let our experts handle everything. 
              Get a free audit and see how we can transform your social presence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/booking">
                  Get Free Social Media Audit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg" 
                className="font-heading font-semibold text-lg px-8 py-6"
              >
                <Link to="/maintenance">View Maintenance Plans</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SocialMedia;